import { ElevatedSurface, Field, SingleSelectDropdown, Text } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { ParameterConfigForm } from "./ParameterConfigForm";
import type { EvalImageParamsState } from "./useEvalImageParams";
import styles from "./EvaluationParametersPanel.module.css";

// Docker 이미지 태그와 그 태그가 공개한 평가 파라미터를 편집한다.

const SUMMARY_PLACEHOLDER = "Docker Image 와 파라미터를 선택해 주세요.";

export function EvaluationParametersPanel({
  params,
  disabled = false,
}: {
  params: EvalImageParamsState;
  disabled?: boolean;
}): ReactNode {
  const options = useMemo(
    () =>
      params.tags.map((entry) => ({
        value: entry.tag,
        // 태그와 추천 표시 사이에 공백 두 칸을 둔다.
        label: entry.recommended ? `${entry.tag}  (추천)` : entry.tag,
      })),
    [params.tags],
  );

  return (
    // 두 호스트(파라미터 모달과 제출 창)는 모두 `surfaceRaised` 위에 있다. 그래서 같은 톤의 패널은
    // 호스트와 구분되지 않는다. 한 단계 위 elevation 은 톤·그림자·dark hairline 을 모두 포함한다.
    // 라이트 테마에서는 그림자가 주된 신호이므로, 이 elevation 덕분에 패널이 별도 표면으로 보인다.
    <ElevatedSurface
      as="section"
      elevation={3}
      hoverElevation={false}
      borderRadius="var(--c6-shape-radius-control)"
      className={styles.card}
    >
      <div className={styles.header}>
        <SlidersIcon />
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.title}>
          Evaluation 파라미터
        </Text>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.summary}>
          {params.selectedTag === "" ? SUMMARY_PLACEHOLDER : params.selectedTag}
        </Text>
      </div>

      <div className={styles.content}>
        <div className={styles.imageBlock}>
          {params.status === "error" ? (
            <ErrorRow message={params.errorMessage} onRetry={params.retryImages} />
          ) : (
            // 드롭다운은 선택 값만 표시한다. 그래서 보이는 제목은 Field 가 소유한다.
            <Field
              label="Docker Image Tag"
              textVariant={hatcheryTextModes["caption-bold"]}
              layout="stack"
              disabled={disabled || options.length === 0}
            >
              <SingleSelectDropdown
                mode={hatcheryDropdownModes.default}
                fullWidth
                menuWidth="matchTrigger"
                label="Docker Image Tag"
                // 고른 Tag 가 없으면 null 이다. c6 는 null 일 때만 placeholder 를 표시한다. 빈
                // 문자열을 넘기면 trigger 가 빈 값으로 남고 아래 안내가 나오지 않는다.
                value={params.selectedTag === "" ? null : params.selectedTag}
                options={options}
                onValueChange={params.selectTag}
                // Tag 가 없으면 드롭다운이 꺼져서 메뉴의 빈 문구가 보이지 않는다. 그래서 같은 문장을
                // placeholder 로도 알린다.
                placeholder={
                  params.status === "loading"
                    ? "Docker Image Tag 를 불러오는 중입니다."
                    : options.length === 0
                      ? "사용할 수 있는 Docker Image Tag 가 없습니다."
                      : "Docker Image Tag 를 선택해 주세요."
                }
                emptyMessage={
                  params.status === "loading"
                    ? "Docker Image Tag 를 불러오는 중입니다."
                    : "사용할 수 있는 Docker Image Tag 가 없습니다."
                }
                disabled={disabled || options.length === 0}
              />
            </Field>
          )}
        </div>

        <div className={styles.paramsBlock}>
          {params.selectedTag === "" ? (
            // 고른 Tag 가 없다. Tag 목록을 받는 중이거나 받지 못했거나 Tag 가 없으면 위의 드롭다운과
            // 오류 줄이 알린다. 여기서 "파라미터가 없습니다"를 표시하면 받지 않은 것을 없다고 말한다.
            params.status === "success" && options.length > 0 ? (
              <EmptyState size="sm">
                Docker Image Tag 를 선택하면 조정할 수 있는 파라미터가 표시됩니다.
              </EmptyState>
            ) : null
          ) : // Tag 를 고른 직후에는 조회 effect 가 아직 돌지 않아 idle 이다. 그 프레임도 대기다.
          params.specsStatus === "loading" || params.specsStatus === "idle" ? (
            <LoadingState size="sm">Evaluation 파라미터를 불러오는 중입니다.</LoadingState>
          ) : params.specsStatus === "error" ? (
            <ErrorRow message={params.specsErrorMessage} onRetry={params.retrySpecs} />
          ) : params.specs.length === 0 ? (
            <EmptyState size="sm">이 Tag 에는 조정할 수 있는 파라미터가 없습니다.</EmptyState>
          ) : (
            <ParameterConfigForm
              specs={params.specs}
              values={params.values}
              onChange={params.setValue}
              onReset={params.resetValues}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    </ElevatedSurface>
  );
}

function ErrorRow({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}): ReactNode {
  return (
    <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
      {message ?? "Evaluation 정보를 불러오지 못했습니다. 다시 시도해 주세요."}
    </EmptyState>
  );
}

// 장식 아이콘 하나 때문에 아이콘 패키지를 추가하지 않는다.
function SlidersIcon(): ReactNode {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M2 4h8M12.5 4H14M2 8h2.5M7 8h7M2 12h6M10.5 12H14" />
      <circle cx="11" cy="4" r="1.5" />
      <circle cx="5.5" cy="8" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
    </svg>
  );
}
